import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import config from '../config';
import Cookies from 'js-cookie';
import Notification from '../components/Notification';
import ReportModal from '../components/ReportModal';
import BlockConfirmModal from '../components/BlockConfirmModal';
import PremiumCarousel from '../components/PremiumCarousel';
import { CarouselSkeleton } from '../components/Skeletons';
import { useUserProducts } from '../hooks/useUserProducts';

import {
    UserDetailsSkeleton,
    ProductGridSkeleton,
    RatingsSkeleton,
} from '../components/Skeletons';
import formatUrl from '../utils/urlFormatter';
import { FaStar, FaWallet } from 'react-icons/fa';
import ProductsGridV2 from '../components/ProductsGrid'; // Import the ProductsGrid component
import '../styles/UserCorner.css';
import '../styles/productDetails.css'; // Import productDetails.css for grid layout
import moderationService from '../services/moderationService';

const UserCorner = () => {
    const navigate = useNavigate();
    const { userUuid } = useParams();  // Changed from userId to userUuid
    const [notification, setNotification] = useState({
        message: '',
        type: 'info',
        header: ''
    });
    const [showReportModal, setShowReportModal] = useState(false);
    const [showBlockModal, setShowBlockModal] = useState(false);
    const [userDetails, setUserDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isFollowing, setIsFollowing] = useState(false); // Add state to track follow status
    const [isOwnProfile, setIsOwnProfile] = useState(false); // Add state to check if viewing own profile
    const [isAnimating, setIsAnimating] = useState(false); // Animation state
    const wsRef = useRef(null); // WebSocket reference
    
    // Use optimized hook for products (separate endpoint with pagination)
    const {
        products: userProducts,
        loading: productsLoading,
        loadingMore: productsLoadingMore,
        hasMore: productsHasMore,
        error: productsError,
        fetchNextPage: fetchMoreProducts,
        refresh: refreshProducts,
        setProducts: setUserProducts,
    } = useUserProducts(userUuid);  // Pass userUuid instead of userId
    const [rating, setRating] = useState(0); // State to handle rating input
    const [comment, setComment] = useState(''); // State to handle comment input
    const [ratings, setRatings] = useState([]); // State to store existing ratings
    const [averageRating, setAverageRating] = useState(0); // State to store average rating
    const [showRatingPopup, setShowRatingPopup] = useState(false); // State to handle rating popup visibility
    const [loggedInUser, setLoggedInUser] = useState(null); // State to store logged-in user details
    const [promotedProducts, setPromotedProducts] = useState([]); // State for promoted products
    const [loadingPromoted, setLoadingPromoted] = useState(true); // Loading state for promoted products
    const [showDeleteWarning, setShowDeleteWarning] = useState(false); // State for delete warning popup
    const [showUnfollowConfirm, setShowUnfollowConfirm] = useState(false); // State for unfollow confirmation modal
    const [isSubmittingRating, setIsSubmittingRating] = useState(false); // Loading state for rating submission
    const [showMoreMenu, setShowMoreMenu] = useState(false); // State for ⋯ dropdown
    const moreMenuRef = useRef(null); // Ref for click-outside

    // Close more menu on click outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) {
                setShowMoreMenu(false);
            }
        };
        if (showMoreMenu) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showMoreMenu]);

    useEffect(() => {
        const fetchUserDetails = async () => {
            const token = Cookies.get('authToken');
            if (!token) {
                setError('Utilisateur non authentifié');
                setLoading(false);
                return;
            }

            try {
                const response = await fetch(`${config.backendUrl}/users/corner/${userUuid}/`, {  // Use userUuid
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Token ${token}`
                    }
                });

                if (!response.ok) {
                    throw new Error('Échec de la récupération des détails de l\'utilisateur');
                }

                const data = await response.json();
                // No products field in response anymore - loaded separately via hook
                setUserDetails(data);

                // Check if the current user is following this user
                const currentUserResponse = await fetch(`${config.backendUrl}/users/me/`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Token ${token}`
                    }
                });

                if (currentUserResponse.ok) {
                    const currentUserData = await currentUserResponse.json();
                    setLoggedInUser(currentUserData);
                    setIsOwnProfile(currentUserData.id === data.id);
                    const isFollowing = data.followers.some(follower => follower.id === currentUserData.id);
                    setIsFollowing(isFollowing);
                }

                // Fetch existing ratings - use UUID
                const userIdentifier = data.uuid || data.id;
                const ratingsResponse = await fetch(`${config.backendUrl}/users/rate/${userIdentifier}/`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Token ${token}`
                    }
                });

                if (ratingsResponse.ok) {
                    const ratingsData = await ratingsResponse.json();
                    setRatings(ratingsData);
                    calculateAverageRating(ratingsData); // Calculate average rating
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchUserDetails();
    }, [userUuid]); // Changed from userId to userUuid

    // WebSocket connection for real-time follow updates
    useEffect(() => {
        if (!loggedInUser?.id) return;

        const connectWebSocket = () => {
            const token = Cookies.get('authToken');
            if (!token) return;

            const wsUrl = config.websocketUrl || config.backendUrl.replace('http', 'ws');
            const ws = new WebSocket(`${wsUrl}notifications/${loggedInUser.id}/?token=${token}`);

            ws.onopen = () => {
                console.log('WebSocket connected for notifications');
            };

            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    
                    // Handle new follower notification (someone followed you)
                    if (data.type === 'notification_new' && data.notification?.type === 'new_follower') {
                        const followerId = data.notification?.data?.follower_id;
                        
                        // If viewing your own profile and someone followed you
                        if (isOwnProfile && userDetails) {
                            setUserDetails(prev => ({
                                ...prev,
                                follower_count: (prev.follower_count || 0) + 1
                            }));
                        }
                        
                        // If viewing the follower's profile
                        if (userDetails?.id === followerId) {
                            setUserDetails(prev => ({
                                ...prev,
                                following_count: (prev.following_count || 0) + 1
                            }));
                        }
                    }
                    
                    // Handle unfollow notification (someone unfollowed you)
                    if (data.type === 'notification_new' && data.notification?.type === 'user_unfollow') {
                        const unfollowerId = data.notification?.data?.sender_id;
                        
                        // If viewing your own profile and someone unfollowed you
                        if (isOwnProfile && userDetails) {
                            setUserDetails(prev => ({
                                ...prev,
                                follower_count: Math.max(0, (prev.follower_count || 0) - 1)
                            }));
                        }
                        
                        // If viewing the unfollower's profile
                        if (userDetails?.id === unfollowerId) {
                            setUserDetails(prev => ({
                                ...prev,
                                following_count: Math.max(0, (prev.following_count || 0) - 1)
                            }));
                        }
                    }
                } catch (err) {
                    console.error('Error parsing WebSocket message:', err);
                }
            };

            ws.onerror = (error) => {
                console.error('WebSocket error:', error);
            };

            ws.onclose = () => {
                console.log('WebSocket disconnected, attempting reconnect...');
                setTimeout(connectWebSocket, 3000); // Reconnect after 3 seconds
            };

            wsRef.current = ws;
        };

        connectWebSocket();

        return () => {
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
        };
    }, [loggedInUser?.id, isOwnProfile, userDetails?.id]);

    // Fetch promoted products for this user
    useEffect(() => {
        const fetchPromotedProducts = async () => {
            // Only fetch if userUuid is valid
            if (!userUuid) return;
            
            setLoadingPromoted(true);
            try {
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s timeout
                
                const response = await fetch(`${config.backendUrl}/products/promoted/user/${userUuid}/`, {
                    method: 'GET',
                    signal: controller.signal
                });

                clearTimeout(timeoutId);

                if (response.ok) {
                    const data = await response.json();
                    setPromotedProducts(data.results || []);
                } else {
                    console.error('Failed to fetch user promoted products');
                    setPromotedProducts([]);
                }
            } catch (err) {
                if (err.name !== 'AbortError') {
                    console.error('Error fetching user promoted products:', err);
                }
                setPromotedProducts([]);
            } finally {
                setLoadingPromoted(false);
            }
        };

        // Defer promoted products to load after main content
        const timer = setTimeout(() => {
            fetchPromotedProducts();
        }, 300); // Wait 300ms before loading promoted products

        return () => clearTimeout(timer);
    }, [userUuid]);
    
    const handleProductClick = (productUuid, event) => {  // Changed to productUuid, added event
        // Add animation effect
        const card = event?.currentTarget;
        if (card) {
            card.classList.add('clicked');
            setTimeout(() => {
                card.classList.remove('clicked');
            }, 600);
        }
        navigate(`/products/view/${productUuid}`);
    };

    const handleDeleteAccountClick = () => {
        setShowDeleteWarning(true);
    };

    const handleConfirmDelete = () => {
        setShowDeleteWarning(false);
        navigate('/delete-account');
    };

    const handleCancelDelete = () => {
        setShowDeleteWarning(false);
    };

    const handleReportSubmit = async (reportData) => {
        try {
            // Build the frontend URL for the user profile
            const profileUrl = `${window.location.origin}/user/${userDetails.uuid || userDetails.id}`;
            
            await moderationService.submitReport({
                target_type: 'profile',
                target_id: userDetails.uuid || userDetails.id,
                reason: reportData.reason,
                details: reportData.description, // Changed from 'description' to 'details'
                target_url: profileUrl,
            });
            
            setShowReportModal(false);
            setNotification({
                message: 'Votre signalement a été envoyé avec succès. Notre équipe l\'examinera dans un délai de 24 heures.',
                type: 'success',
                header: 'Signalement envoyé',
            });
        } catch (error) {
            setNotification({
                message: error.message || 'Erreur lors de l\'envoi du signalement. Veuillez réessayer.',
                type: 'error',
                header: 'Erreur',
            });
        }
    };

    const handleBlockUser = async (reason) => {
        try {
            await moderationService.blockUser(userDetails.uuid || userDetails.id, reason);
            
            setShowBlockModal(false);
            setNotification({
                message: `${userDetails.username} a été bloqué avec succès. Vous ne pourrez plus échanger avec cet utilisateur.`,
                type: 'success',
                header: 'Utilisateur bloqué',
            });
            
            // Optionally redirect or update UI
            setTimeout(() => {
                navigate('/blocked-users');
            }, 2000);
        } catch (error) {
            setNotification({
                message: error.message || 'Erreur lors du blocage de l\'utilisateur. Veuillez réessayer.',
                type: 'error',
                header: 'Erreur',
            });
        }
    };

    const calculateAverageRating = (ratings) => {
        if (ratings.length === 0) {
            setAverageRating(0);
            return;
        }
        const total = ratings.reduce((sum, rating) => sum + rating.rating, 0);
        const average = total / ratings.length;
        setAverageRating(average); // Store as a number to allow formatting in render
    };

    const handleFollow = async () => {
        const token = Cookies.get('authToken');
        if (!token) {
            setError('Utilisateur non authentifié');
            return;
        }

        // Instant UI feedback with animation
        setIsFollowing(true);
        setIsAnimating(true);
        setUserDetails(prev => ({
            ...prev,
            follower_count: (prev?.follower_count || 0) + 1
        }));

        try {
            const response = await fetch(`${config.backendUrl}/users/follow/${userUuid}/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Token ${token}`
                }
            });

            if (!response.ok) {
                // Revert on error
                setIsFollowing(false);
                setUserDetails(prev => ({
                    ...prev,
                    follower_count: Math.max(0, (prev?.follower_count || 0) - 1)
                }));
                setNotification({ 
                    message: "Échec de l'abonnement à l'utilisateur, Réessayer plus tard", 
                    type: 'error', 
                    header: "Échec pendant l'abonnement" 
                });
            } else {
                setNotification({ 
                    message: 'Vous êtes abonné!', 
                    type: 'success', 
                    header: "Abonnement réussi" 
                });
            }
        } catch (err) {
            // Revert on error
            setIsFollowing(false);
            setUserDetails(prev => ({
                ...prev,
                follower_count: Math.max(0, (prev?.follower_count || 0) - 1)
            }));
            setError(err.message);
        } finally {
            setTimeout(() => setIsAnimating(false), 600); // Reset animation after 600ms
        }
    };

    const handleUnfollow = async () => {
        const token = Cookies.get('authToken');
        if (!token) {
            setError('Utilisateur non authentifié');
            return;
        }

        // Close the confirmation modal
        setShowUnfollowConfirm(false);

        // Instant UI feedback with animation
        setIsFollowing(false);
        setIsAnimating(true);
        setUserDetails(prev => ({
            ...prev,
            follower_count: Math.max(0, (prev?.follower_count || 0) - 1)
        }));

        try {
            const response = await fetch(`${config.backendUrl}/users/unfollow/${userUuid}/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Token ${token}`
                }
            });

            if (!response.ok) {
                // Revert on error
                setIsFollowing(true);
                setUserDetails(prev => ({
                    ...prev,
                    follower_count: (prev?.follower_count || 0) + 1
                }));
                setNotification({ 
                    message: "Échec du désabonnement de l'utilisateur, Réessayer plus tard", 
                    type: 'error', 
                    header: "Échec pendant le désabonnement" 
                });
            } else {
                setNotification({ 
                    message: 'Vous vous êtes désabonné!', 
                    type: 'success', 
                    header: "Désabonnement réussi" 
                });
            }
        } catch (err) {
            // Revert on error
            setIsFollowing(true);
            setUserDetails(prev => ({
                ...prev,
                follower_count: (prev?.follower_count || 0) + 1
            }));
            setError(err.message);
        } finally {
            setTimeout(() => setIsAnimating(false), 600); // Reset animation after 600ms
        }
    };

    const handleRatingSubmit = async (e) => {
        e.preventDefault();
        
        // Validate rating
        if (rating === 0) {
            setNotification({
                message: 'Veuillez sélectionner une note avant de soumettre.',
                type: 'warning',
                header: 'Note requise',
            });
            return;
        }
        
        const token = Cookies.get('authToken');
        if (!token) {
            setError('Utilisateur non authentifié');
            return;
        }

        setIsSubmittingRating(true);

        try {
            const response = await fetch(`${config.backendUrl}/users/rate/${userUuid}/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Token ${token}`
                },
                body: JSON.stringify({ rating, comment })
            });

            if (response.status === 403) {
                // Parse the error message from the backend
                let errorMessage = "Vous devez avoir une conversation avec cet utilisateur avant de pouvoir l'évaluer.";
                try {
                    const errorData = await response.json();
                    if (errorData.error) {
                        errorMessage = errorData.error;
                    }
                } catch (parseError) {

                }
                
                setNotification({
                    message: errorMessage,
                    type: "info",
                    header: "Information",
                });
                setIsSubmittingRating(false);
                return;
            }

            if (!response.ok) {
                // Parse error message from backend for other error statuses
                let errorMessage = 'Échec de l\'évaluation de l\'utilisateur';
                try {
                    const errorData = await response.json();
                    if (errorData.error) {
                        errorMessage = errorData.error;
                    }
                } catch (parseError) {

                }
                throw new Error(errorMessage);
            }

            const newRating = await response.json();
            const updatedRatings = [...ratings, newRating];
            setRatings(updatedRatings); // Add new rating to the list
            calculateAverageRating(updatedRatings); // Recalculate average rating
            
            // Show success notification
            setNotification({
                message: 'Votre évaluation a été envoyée avec succès!',
                type: 'success',
                header: 'Évaluation envoyée',
            });
            
            // Reset form and close modal
            setRating(0);
            setComment('');
            setShowRatingPopup(false);
        } catch (err) {
            setNotification({
                message: err.message || 'Une erreur s\'est produite lors de l\'envoi de votre évaluation.',
                type: 'error',
                header: 'Erreur',
            });
        } finally {
            setIsSubmittingRating(false);
        }
    };
    const handleLike = async (productId, isLikedByUser) => {
        const token = Cookies.get('authToken');
        if (!token) {
            setError('Utilisateur non authentifié');
            return;
        }

        try {
            const url = `${config.backendUrl}/products/${isLikedByUser ? 'unlike' : 'like'}/${productId}/`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Authorization': `Token ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                // Update products from hook instead of userDetails
                setUserProducts((prevProducts) =>
                    prevProducts.map((product) =>
                        product.id === productId
                            ? { 
                                ...product, 
                                likes: data.likes, 
                                liked_by: data.liked 
                                    ? [...(product.liked_by || []), loggedInUser.id] 
                                    : (product.liked_by || []).filter(id => id !== loggedInUser.id) 
                            }
                            : product
                    )
                );
            } else {
                throw new Error(`Échec de ${isLikedByUser ? 'ne pas aimer' : 'aimer'} le produit`);
            }
        } catch (error) {
            setError(`Une erreur s'est produite lors de ${isLikedByUser ? 'ne pas aimer' : 'aimer'} le produit: ${error.message}`);
        }
    };

    const getUserInitials = (firstName, lastName) => {
        const firstInitial = firstName ? firstName.charAt(0).toUpperCase() : '';
        const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : '';
        const initials = firstInitial + lastInitial;
        return initials || 'U'; // Return 'U' for User if no initials available
    };

    if (loading) {
        return (
            <div className="user-corner-container">
                <UserDetailsSkeleton /> {/* Skeleton for user details */}
                <ProductGridSkeleton /> {/* Skeleton for products */}
                <RatingsSkeleton /> {/* Skeleton for ratings */}
            </div>
        );
    }

    if (error) {
        return <div className="error">{error}</div>;
    }
    const handleNotificationClose = () => {
        setNotification({ message: '', type: 'info', header: '' });
    };

    if (!userDetails) {
        return <div className="no-user">Utilisateur non trouvé</div>;
    }

    return (
        <div className="user-corner-container">
            {notification.message && (
                <Notification
                    message={notification.message}
                    type={notification.type}
                    header={notification.header}
                    onClose={handleNotificationClose}
                />
            )}
            <div className="uc-user-details-card">
                <div className="uc-card-banner"></div>
                <div className="uc-profile-picture-container">
                    {(userDetails.profile_picture || userDetails.profile_picture_url) ? (
                        <img 
                            src={formatUrl(userDetails.profile_picture || userDetails.profile_picture_url)} 
                            alt="Profile" 
                            className="uc-profile-picture"
                            onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                            }}
                        />
                    ) : null}
                    <div 
                        className="uc-profile-initials" 
                        style={{ display: (userDetails.profile_picture || userDetails.profile_picture_url) ? 'none' : 'flex' }}
                    >
                        {getUserInitials(userDetails.first_name, userDetails.last_name)}
                    </div>
                </div>
                
                <div className="uc-profile-info">
                    <h1 className="uc-user-name">
                        {userDetails.username}
                    </h1>
                    <p className="uc-user-fullname">
                        {userDetails.first_name} {userDetails.last_name}
                    </p>
                    
                    <div className="uc-stats-container">
                        <div className="uc-stat-item uc-stat-link" onClick={() => navigate(`/users/${userDetails.uuid || userDetails.id}/followers`)} style={{cursor:'pointer'}}>
                            <span className="uc-stat-number">{userDetails.follower_count}</span>
                            <span className="uc-stat-label">Abonnés</span>
                        </div>
                        <div className="uc-stat-divider"></div>
                        <div className="uc-stat-item uc-stat-link" onClick={() => navigate(`/users/${userDetails.uuid || userDetails.id}/following`)} style={{cursor:'pointer'}}>
                            <span className="uc-stat-number">{userDetails.following_count}</span>
                            <span className="uc-stat-label">Abonnements</span>
                        </div>
                        <div className="uc-stat-divider"></div>
                        <div className="uc-stat-item uc-stat-link" onClick={() => navigate(`/products/user/${userDetails.uuid || userDetails.id}`)} style={{cursor:'pointer'}}>
                            <span className="uc-stat-number">{userDetails.product_count || 0}</span>
                            <span className="uc-stat-label">Produits</span>
                        </div>
                        <div className="uc-stat-divider"></div>
                        <div className="uc-stat-item">
                            <span className="uc-stat-number uc-stat-rating">
                                <FaStar size={14} color="#FFD700" style={{marginRight: 3, verticalAlign: 'text-bottom'}} />
                                {typeof averageRating === 'number' ? averageRating.toFixed(1) : '0.0'}
                            </span>
                            <span className="uc-stat-label">Note</span>
                        </div>
                    </div>
                </div>
                
                <div className="uc-user-details">
                    <div className="uc-detail-item">
                        <i className="fas fa-envelope"></i>
                        {userDetails.email ? (
                            <a href={`mailto:${userDetails.email}`} className="email-link">{userDetails.email}</a>
                        ) : (
                            <span>Non spécifié</span>
                        )}
                    </div>
                    <div className="uc-detail-item">
                        <i className="fas fa-phone"></i>
                        {userDetails.mobile_number ? (
                            <a href={`tel:${userDetails.mobile_number}`} className="phone-link">{userDetails.mobile_number}</a>
                        ) : (
                            <span>Non spécifié</span>
                        )}
                    </div>
                    <div className="uc-detail-item">
                        <i className="fas fa-calendar-alt"></i>
                        <span>Membre depuis {new Date(userDetails.date_joined).toLocaleDateString()}</span>
                    </div>
                </div>
                
                <div className="uc-actions">
                    {isOwnProfile ? (
                        <>
                            <button
                                className="uc-btn uc-btn-statistics"
                                onClick={() => navigate(`/user-statistics/${userDetails.uuid || userDetails.id}`)}
                            >
                                <i className="fas fa-chart-line uc-btn-icon"></i>
                                Mes Statistiques
                            </button>
                            <button
                                className="uc-btn uc-btn-primary"
                                onClick={() => navigate('/portefeuille')}
                            >
                                <FaWallet className="uc-btn-icon" />
                                Mon Portefeuille
                            </button>
                        </>
                    ) : (
                        <>
                            {isFollowing ? (
                                <button 
                                    className={`uc-btn uc-btn-following ${isAnimating ? 'btn-pulse' : ''}`}
                                    onClick={() => setShowUnfollowConfirm(true)}
                                    disabled={isAnimating}
                                >
                                    <i className="fas fa-user-check uc-btn-icon"></i>
                                    Abonné
                                </button>
                            ) : (
                                <button 
                                    className={`uc-btn uc-btn-follow ${isAnimating ? 'btn-scale-pop' : ''}`}
                                    onClick={handleFollow}
                                    disabled={isAnimating}
                                >
                                    <i className="fas fa-user-plus uc-btn-icon"></i>
                                    S'abonner
                                </button>
                            )}
                            <button 
                                className="uc-btn uc-btn-secondary"
                                onClick={() => setShowRatingPopup(true)}
                            >
                                <i className="fas fa-star uc-btn-icon"></i>
                                Évaluer
                            </button>
                            <div className="uc-more-wrapper" ref={moreMenuRef}>
                                <button 
                                    className="uc-btn uc-btn-more"
                                    onClick={() => setShowMoreMenu(!showMoreMenu)}
                                    title="Plus d'options"
                                >
                                    <i className="fas fa-ellipsis-h"></i>
                                </button>
                                {showMoreMenu && (
                                    <div className="uc-more-dropdown">
                                        <button 
                                            className="uc-more-item"
                                            onClick={async () => {
                                                setShowMoreMenu(false);
                                                try {
                                                    const result = await moderationService.checkExistingReport('profile', userDetails.uuid || userDetails.id);
                                                    if (result.exists) {
                                                        const reportDate = new Date(result.report.created_at).toLocaleDateString('fr-FR');
                                                        setNotification({
                                                            message: `Vous avez déjà signalé cet utilisateur le ${reportDate}. Notre équipe examinera votre signalement dans un délai de 24 heures.`,
                                                            type: 'info',
                                                            header: 'Signalement déjà envoyé',
                                                        });
                                                    } else {
                                                        setShowReportModal(true);
                                                    }
                                                } catch (error) {
                                                    console.error('Error checking existing report:', error);
                                                    setShowReportModal(true);
                                                }
                                            }}
                                        >
                                            <i className="fas fa-flag"></i>
                                            Signaler
                                        </button>
                                        <button 
                                            className="uc-more-item uc-more-item-danger"
                                            onClick={() => { setShowMoreMenu(false); setShowBlockModal(true); }}
                                        >
                                            <i className="fas fa-ban"></i>
                                            Bloquer
                                        </button>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Promoted Products Section */}
            {!loadingPromoted && promotedProducts.length > 0 && (
                <div className="uc-promoted-products-card">
                    <PremiumCarousel
                        products={promotedProducts}
                        loggedInUser={loggedInUser}
                        onProductClick={handleProductClick}
                        handleLikeClick={handleLike}
                        isLoading={loadingPromoted}
                        title="Produits en vedette"
                        showTitle={false}
                    />
                </div>
            )}
            {loadingPromoted && (
                <div className="uc-promoted-products-card">
                    <CarouselSkeleton />
                </div>
            )}

            {productsLoading ? (
                <div className="uc-products-card">
                    <h2>Produits</h2>
                    <ProductGridSkeleton />
                </div>
            ) : userProducts && userProducts.length > 0 ? (
                <div className="uc-products-card">
                    <h2>Produits ({userDetails.product_count || userProducts.length})</h2>
                    <ProductsGridV2 
                        products={userProducts}
                        handleProductClick={handleProductClick}
                        handleLikeClick={handleLike}
                        loggedInUser={loggedInUser}
                        hasMore={productsHasMore}
                        fetchMoreData={fetchMoreProducts}
                        loading={productsLoadingMore}
                    />
                </div>
            ) : (
                <div className="uc-products-card empty-products">
                    <h2>Produits</h2>
                    {isOwnProfile ? (
                        <div className="empty-products-content">
                            <p>Vous n'avez pas encore de produits.</p>
                            <button
                                className="add-post-button"
                                onClick={() => navigate('/add-post')}
                            >
                                <i className="fas fa-plus"></i> Ajouter un produit
                            </button>
                        </div>
                    ) : (
                        <p>Cet utilisateur n'a pas encore de produits.</p>
                    )}
                </div>
            )}

            {ratings.length > 0 ? (
                <div className="rating-section">
                    <h2>Commentaires et évaluations</h2>
                    <div className="ratings-list">
                        {ratings.map((rating, index) => (
                            <div key={index} className="rating-item">
                                <div className="rating-header">
                                    {(rating.rater?.profile_picture || rating.rater?.profile_picture_url) ? (
                                        <span onClick={() => navigate(`/user/corner/${rating.rater?.uuid || rating.rater?.id}/`)} style={{cursor: 'pointer'}}>
                                            <img 
                                                src={formatUrl(rating.rater.profile_picture || rating.rater.profile_picture_url)} 
                                                alt="Rater" 
                                                className="rater-profile-picture"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.nextSibling.style.display = 'flex';
                                                }}
                                            />
                                            <div 
                                                className="rater-initials" 
                                                style={{display: 'none', cursor: 'pointer', background: '#003366', color: '#fff', alignItems: 'center', justifyContent: 'center'}}
                                                onClick={() => navigate(`/user/corner/${rating.rater?.uuid || rating.rater?.id}/`)}
                                            >
                                                {`${rating.rater?.first_name?.charAt(0) || ''}${rating.rater?.last_name?.charAt(0) || ''}`.toUpperCase() || 'U'}
                                            </div>
                                        </span>
                                    ) : (
                                        <div 
                                            className="rater-initials" 
                                            onClick={() => navigate(`/user/corner/${rating.rater?.uuid || rating.rater?.id}/`)}
                                            style={{cursor: 'pointer', background: '#003366', color: '#fff', alignItems: 'center', justifyContent: 'center'}}
                                        >
                                            {`${rating.rater?.first_name?.charAt(0) || ''}${rating.rater?.last_name?.charAt(0) || ''}`.toUpperCase() || 'U'}
                                        </div>
                                    )}
                                    <div className="rater-details">
                                        <p className="rater-name" style={{cursor: 'pointer', margin: 0, fontWeight: 600}}
                                           onClick={() => navigate(`/user/corner/${rating.rater?.uuid || rating.rater?.id}/`)}
                                        >
                                            {rating.rater?.first_name} {rating.rater?.last_name}
                                        </p>
                                        <div className="rater-stars">
                                            {[...Array(5)].map((star, i) => (
                                                <FaStar
                                                    key={i}
                                                    color={i < rating.rating ? "#FFD700" : "#e4e5e9"}
                                                    size={16}
                                                    className="user-rating-star"
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <p className="rating-comment">{rating.comment}</p>
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div className="rating-section">
                    <h2>Commentaires et évaluations</h2>
                    <div className="uc-empty-ratings">
                        <i className="fas fa-comments"></i>
                        <p>Aucune évaluation pour le moment.</p>
                        {!isOwnProfile && (
                            <button className="uc-btn uc-btn-secondary" onClick={() => setShowRatingPopup(true)}>
                                <i className="fas fa-star uc-btn-icon"></i>
                                Soyez le premier à évaluer
                            </button>
                        )}
                    </div>
                </div>
            )}

            {showRatingPopup && (
                <div className="rating-popup">
                    <div className="rating-popup-content">
                        <span className="close-popup-icon" onClick={() => { 
                            setShowRatingPopup(false); 
                            setRating(0);
                            setComment('');
                        }}>&times;</span>
                        <h2>Évaluer cet utilisateur</h2>
                        <form onSubmit={handleRatingSubmit} className="rating-form">
                            <div className="stars">
                                {[...Array(5)].map((star, index) => {
                                    const ratingValue = index + 1;
                                    return (
                                        <label key={index}>
                                            <input
                                                className='star-radio'
                                                type="radio"
                                                name="rating"
                                                value={ratingValue}
                                                onClick={() => setRating(ratingValue)}
                                                disabled={isSubmittingRating}
                                            />
                                            <FaStar
                                                className="star"
                                                color={ratingValue <= rating ? "#FFD700" : "#e4e5e9"}
                                                size={24}
                                            />
                                        </label>
                                    );
                                })}
                            </div>
                            <div className="input-group">
                                <label>Commentaire (optionnel):</label>
                                <textarea
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                    disabled={isSubmittingRating}
                                    placeholder="Partagez votre expérience avec cet utilisateur..."
                                    maxLength={500}
                                />
                                <div className="character-count">
                                    {comment.length}/500
                                </div>
                            </div>
                            <div className="rating-form-actions">
                                <button 
                                    type="button" 
                                    className="cancel-rating-button"
                                    onClick={() => {
                                        setShowRatingPopup(false);
                                        setRating(0);
                                        setComment('');
                                    }}
                                    disabled={isSubmittingRating}
                                >
                                    <i className="fas fa-times"></i>
                                    Annuler
                                </button>
                                <button 
                                    type="submit" 
                                    className="submit-rating-button"
                                    disabled={isSubmittingRating || rating === 0}
                                >
                                    {isSubmittingRating ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i>
                                            Envoi en cours...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-paper-plane"></i>
                                            Soumettre
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Account Warning Popup */}
            {showDeleteWarning && (
                <div className="delete-warning-overlay">
                    <div className="delete-warning-popup">
                        <div className="delete-warning-icon">
                            <i className="fas fa-exclamation-triangle"></i>
                        </div>
                        <h2>Supprimer votre compte ?</h2>
                        <p className="delete-warning-text">
                            Cette action est <strong>irréversible</strong>. Toutes vos données seront supprimées définitivement, incluant :
                        </p>
                        <ul className="delete-warning-list">
                            <li><i className="fas fa-box"></i> Vos produits</li>
                            <li><i className="fas fa-comments"></i> Vos messages</li>
                            <li><i className="fas fa-wallet"></i> Votre portefeuille</li>
                            <li><i className="fas fa-users"></i> Vos abonnés et abonnements</li>
                            <li><i className="fas fa-star"></i> Vos évaluations</li>
                        </ul>
                        <div className="delete-warning-actions">
                            <button 
                                className="uc-btn uc-btn-cancel"
                                onClick={handleCancelDelete}
                            >
                                <i className="fas fa-times"></i>
                                Annuler
                            </button>
                            <button 
                                className="uc-btn uc-btn-confirm-delete"
                                onClick={handleConfirmDelete}
                            >
                                <i className="fas fa-check"></i>
                                Continuer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Unfollow Confirmation Modal */}
            {showUnfollowConfirm && (
                <div className="delete-warning-overlay">
                    <div className="delete-warning-popup unfollow-confirm-popup">
                        <div className="delete-warning-icon unfollow-icon">
                            <i className="fas fa-user-minus"></i>
                        </div>
                        <h2>Se désabonner ?</h2>
                        <p className="delete-warning-text">
                            Voulez-vous vraiment vous désabonner de <strong>{userDetails?.username}</strong> ?
                        </p>
                        <p className="delete-warning-text" style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
                            Vous ne recevrez plus de notifications sur leurs nouveaux produits.
                        </p>
                        <div className="delete-warning-actions">
                            <button 
                                className="uc-btn uc-btn-cancel"
                                onClick={() => setShowUnfollowConfirm(false)}
                            >
                                <i className="fas fa-times"></i>
                                Annuler
                            </button>
                            <button 
                                className="uc-btn uc-btn-confirm-delete"
                                onClick={handleUnfollow}
                            >
                                <i className="fas fa-user-minus"></i>
                                Se désabonner
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Report Modal */}
            {showReportModal && (
                <ReportModal
                    show={showReportModal}
                    onClose={() => setShowReportModal(false)}
                    onSubmit={handleReportSubmit}
                    targetType="profile"
                    targetName={`${userDetails?.first_name || ''} ${userDetails?.last_name || ''}`.trim() || userDetails?.username}
                />
            )}
            
            {/* Block Confirm Modal */}
            {showBlockModal && (
                <BlockConfirmModal
                    show={showBlockModal}
                    onClose={() => setShowBlockModal(false)}
                    onConfirm={handleBlockUser}
                    userName={`${userDetails?.first_name || ''} ${userDetails?.last_name || ''}`.trim() || userDetails?.username}
                />
            )}
        </div>
    );
};

export default UserCorner;